/**
 * Related-spaces resource: TanStack-backed read + mutation hooks for a
 * space's related-spaces sub-resource.
 *
 * One query hook (`useRelatedSpaces`) and one mutation hook per write
 * (`useSelectRelatedSpace`, `useUnselectRelatedSpace`,
 * `useReorderRelatedSpaces`), each auto-invalidating the read query on
 * success so a mounted list refetches without the caller wiring that up
 * itself.
 */
import type { RelatedSpace } from '@repo/shared/orpc/procedures/related-spaces';
import { useApiQueryUtils } from 'system/api-client-context';
import { type Datastore, useDatastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import { useInstanceMutation } from './use-silo-mutation';
import { useSiloQuery } from './use-silo-query';

export type { RelatedSpace } from '@repo/shared/orpc/procedures/related-spaces';

/** A space's related spaces, in stored order. Empty while loading. */
export function useRelatedSpaces(spaceKey: string): { relatedSpaces: RelatedSpace[]; isLoading: boolean } {
    const utils = useApiQueryUtils();
    const query = useSiloQuery({ procedure: utils.spaces.relatedSpaces.list, input: { spaceKey } });
    return { relatedSpaces: query.data?.items ?? [], isLoading: query.isLoading };
}

function invalidateRelatedSpaces(datastore: Datastore) {
    const queryClient = datastore.getQueryClient();
    const utils = datastore.getApiQueryUtils();
    fireAndForget(
        queryClient.invalidateQueries({
            queryKey: utils.spaces.relatedSpaces.list.key({
                input: { siloKey: datastore.getSiloKey(), spaceKey: datastore.getInstanceKey() },
            }),
        }),
    );
}

/** Select a related space. Auto-invalidates the list on success. */
export function useSelectRelatedSpace() {
    const datastore = useDatastore();
    const utils = useApiQueryUtils();
    const mutation = useInstanceMutation({
        procedure: utils.spaces.relatedSpaces.select,
        onSuccess: () => invalidateRelatedSpaces(datastore),
    });
    return { ...mutation, selectRelatedSpaceAsync: mutation.mutateAsync };
}

/** Unselect a related space. Auto-invalidates the list on success. */
export function useUnselectRelatedSpace() {
    const datastore = useDatastore();
    const utils = useApiQueryUtils();
    const mutation = useInstanceMutation({
        procedure: utils.spaces.relatedSpaces.unselect,
        onSuccess: () => invalidateRelatedSpaces(datastore),
    });
    return { ...mutation, unselectRelatedSpaceAsync: mutation.mutateAsync };
}

/** Reorder related spaces. Auto-invalidates the list on success. */
export function useReorderRelatedSpaces() {
    const datastore = useDatastore();
    const utils = useApiQueryUtils();
    const mutation = useInstanceMutation({
        procedure: utils.spaces.relatedSpaces.reorder,
        onSuccess: () => invalidateRelatedSpaces(datastore),
    });
    return { ...mutation, reorderRelatedSpacesAsync: mutation.mutateAsync };
}
